import type { ArticleBlock } from '~/content/learn';

/**
 * Company announcements and press releases shown under /news.
 *
 * Kept in code rather than Payload so a release ships with the deploy that
 * announces it. Bodies reuse the Learn article block model, so the detail page
 * renders them with the same typography as /article/*.
 */
export interface NewsArticle {
  slug: string;
  title: string;
  /** Standfirst shown under the headline. */
  subtitle?: string;
  /** ISO date (YYYY-MM-DD), used for ordering, JSON-LD and the sitemap. */
  publishedAt: string;
  /** Human-readable date shown on the page. */
  date: string;
  /** One or two sentences for the overview list and meta description. */
  excerpt: string;
  body: ArticleBlock[];
}

export const NEWS_URL_PREFIX = 'news';

const newsArticles: NewsArticle[] = [
  {
    slug: 'nsave-fca-authorisation-emi',
    title:
      'nsave marks major milestone in journey to build global financial institution with FCA authorisation as EMI',
    subtitle: 'Expands board with two appointments to Board Risk Committee',
    publishedAt: '2026-09-28',
    date: 'Sep 28, 2026',
    excerpt:
      'nsave has been authorised by the UK Financial Conduct Authority as an Electronic Money Institution, and welcomes Peter O’Higgins and Julian Gooding to its newly formed Board Risk Committee.',
    body: [
      {
        type: 'paragraph',
        text: 'London, UK, 28th Sep 2026. nsave Ltd (“nsave”), the global financial platform built to expand access to financial services for people from emerging markets, has been authorised by the UK Financial Conduct Authority (FCA) as an Electronic Money Institution (EMI) in recognition of its robust governance, compliance and risk management. As an authorised EMI, nsave can issue electronic money and provide payment services directly to customers around the world.',
      },
      {
        type: 'paragraph',
        text: 'nsave also announces that Peter O’Higgins and Julian Gooding are joining its newly formed Board Risk Committee.',
      },
      { type: 'paragraph', text: 'Amer Baroudi, co-founder and CEO of nsave, said:' },
      {
        type: 'paragraph',
        text: '“When we started nsave, we believed that place of birth shouldn’t determine whether you have fair and easy access to the global financial system. Becoming an FCA-authorised EMI reflects the robust technology underpinning our products and the strength of our governance, compliance and risk management.',
      },
      {
        type: 'paragraph',
        text: 'We are particularly proud to have achieved this authorisation in one of the world’s most sophisticated regulatory regimes and one of the best places in the world to build ambitious, globally relevant financial companies.',
      },
      {
        type: 'paragraph',
        text: 'Furthermore, the appointment of experienced professionals of the caliber of Peter and Julian to our Board Risk Committee reflects the significant distance nsave has travelled in our journey to become a global financial institution, providing fair and easy access for emerging market customers. They’ll bring a depth of experience in fintech, finance and regulation that will be critical for nsave as we move into our next phase of growth.”',
      },
      {
        type: 'paragraph',
        text: 'The FCA EMI authorisation follows significant investment in nsave’s technology, governance, compliance and risk management capabilities, as the company has continued to develop its products and services. Since the start of the year, nsave has expanded from a global account into a financial platform adding broader payment capabilities into the wider nsave offering, while growing its customer base across multiple international markets.',
      },
      { type: 'heading', text: 'About nsave', level: 2 },
      {
        type: 'paragraph',
        text: 'nsave is a global financial platform built to expand access to financial services for people from emerging markets, and backed by TQ Ventures, Sequoia Capital, Y Combinator and other leading global technology investors.',
      },
      {
        type: 'paragraph',
        text: 'Headquartered in London, nsave provides customers with access to a range of financial products and services in emerging markets around the world with the belief that your country of birth shouldn’t be a block to the global financial system. The availability of individual products and the entity or partner providing them varies by jurisdiction.',
      },
      { type: 'heading', text: 'About Peter O’Higgins', level: 2 },
      {
        type: 'paragraph',
        text: 'Peter has more than 20 years of experience in product and finance, including scaling a fintech from start-up to global business. Peter was Revolut’s first CFO, joining when it had around 40 people and no revenue, and built the finance infrastructure that helped it raise over $315m, reach 4.5 million customers and secure a banking licence. Prior to Revolut, he spent twelve years at J.P. Morgan in London and New York, and has since been CFO at Money Dashboard and Managing Director for the UK and Europe at Brex.',
      },
      { type: 'heading', text: 'About Julian Gooding', level: 2 },
      {
        type: 'paragraph',
        text: 'Julian has thirty years of experience in legal, compliance and regulatory work across global banks, private practice and regulators. A senior regulatory lawyer, he spent his career at the FCA, Credit Suisse and Magic Circle law firms, holding senior roles in Asia, Zurich and London. These included General Counsel for APAC, GC for the Corporate Legal Group and GC COO, as well as Deputy Chief Compliance Officer and Chief Compliance Officer for Regulatory Compliance.',
      },
      { type: 'heading', text: 'Regulatory information', level: 2 },
      {
        type: 'paragraph',
        text: 'nsave Ltd is authorised by the Financial Conduct Authority as an Electronic Money Institution under the Electronic Money Regulations 2011 (FRN 1047531).',
      },
      { type: 'heading', text: 'Media enquiries', level: 2 },
      {
        type: 'paragraph',
        text: 'press@nsave.com',
        inline: [{ text: 'press@nsave.com', href: 'mailto:press@nsave.com' }],
      },
    ],
  },
];

/** Newest first. */
export function listNewsArticles(): NewsArticle[] {
  return [...newsArticles].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
}

export function getNewsArticle(slug: string): NewsArticle | undefined {
  return newsArticles.find((article) => article.slug === slug);
}

export function newsArticleHref(article: Pick<NewsArticle, 'slug'>): string {
  return `/${NEWS_URL_PREFIX}/${article.slug}`;
}
